
import { AsyncPipe } from "@angular/common";
import {
    AfterViewInit,
    Component,
    OnDestroy,
    OnInit,
    ViewChild,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { ActionButtonComponent } from "@components/action-button/action-button.component";
import { InlineToastComponent } from "@components/inline-toast/inline-toast.component";
import { TogglePanelComponent as TogglePanelComponent_1 , TogglePanelComponent } from "@components/toggle-panel/toggle-panel.component";
import { Observable, Subject, combineLatest } from "rxjs";
import { filter, map, takeUntil } from "rxjs/operators";

import { EntityDTO } from "~app/common/models/entity-models";
import { canEdit } from "~app/role-permissions";
import { SkogsvardEntity } from "../../models/atgard-models";
import { AtgardKedjaMediatorService } from "../../services/atgardkedja-mediator.service";
import { KontraktMediatorService } from "../../services/kontrakt-mediator.service";
import { NavigationService } from "../../services/navigation.service";
import { VKAtgardHelper } from "../../utils/vkatgard-helper";
import { SkogsvardItemComponent } from "../skogsvard-item/skogsvard-item.component";
@Component({
    selector: "rask-skogsvard-list",
    templateUrl: "./skogsvard-list.component.html",
    styleUrls: ["./skogsvard-list.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        TogglePanelComponent_1,
        ActionButtonComponent,
        InlineToastComponent,
        SkogsvardItemComponent,
        AsyncPipe
    ]
})
export class SkogsvardListComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild(TogglePanelComponent) togglePanel!: TogglePanelComponent;

    protected isReadonly$: Observable<boolean>;

    /**
     * Om användaren skapar en skogsvårdsåtgärd så ska panelen öppnas automatiskt.
     * Om panelen stängs av någon anledning så ska panelen aldrig öppnas automatiskt igen.
     */
    protected neverAutoExpandOnNewSkogsvardCreatedAgain = false;
    protected skogsvardAdded$ = new Subject<void>();
    protected skogsvardList$: Observable<SkogsvardEntity[]>;
    protected skogsvardListSorted: SkogsvardEntity[];

    private readonly atgardKedjaMediatorService = inject(AtgardKedjaMediatorService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly navigationService = inject(NavigationService);
    private readonly ngUnsubscribe$ = new Subject<void>();

    private readonly canEdit = canEdit();

    constructor() {
        this.isReadonly$ = this.kontraktMediatorService.isReadonly$.pipe(
            map(readonly => readonly || !this.canEdit())
        );
    }

    ngAfterViewInit() {
        combineLatest([
            this.skogsvardList$,
            this.navigationService.atgardId$
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(([
                entityLista,
                atgardId
            ]) => {
                if (atgardId) {
                    this.togglePanel.forceExpandedState(
                        !!entityLista.find(x => x.id === atgardId)
                    );
                }
            });
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    ngOnInit(): void {
        const atgarderPrel$ = this.kontraktMediatorService.atgarderPreload$;

        this.skogsvardList$ = atgarderPrel$.pipe(
            takeUntil(this.ngUnsubscribe$),
            map(atg => atg.filter(x => VKAtgardHelper.isAtgardEntitySkogsvard(x)) as SkogsvardEntity[])
        );

        combineLatest([
            this.skogsvardList$,
            this.atgardKedjaMediatorService.kontraktKedjor$
        ]).pipe(
            takeUntil(this.ngUnsubscribe$)
        )
            .subscribe(([
                entities,
                uppdated
            ]) => {
                if (!uppdated) {
                    return;
                }
                this.skogsvardListSorted = this.atgardKedjaMediatorService.getSorteradAtgardslista(entities);
            });

        this.kontraktMediatorService.atgardAdded$
            .pipe(
                takeUntil(this.ngUnsubscribe$),
                filter(x => VKAtgardHelper.isAtgardEntitySkogsvard(x))
            ).subscribe(() => {
                this.skogsvardAdded$.next();
                if (!this.neverAutoExpandOnNewSkogsvardCreatedAgain) {
                    this.togglePanel.forceExpandedState(true);
                }
            });
    }

    protected kontraktIsViol3(): boolean {
        return this.kontraktMediatorService.kontrakt.viol3;
    }

    protected isSkogsvardFPVKontrakt():  boolean {
        return this.kontraktMediatorService.kontrakt.isSkogsvardFPVKontrakt;
    }

    protected isVacsAvraknatOrAvslutat(): boolean {
        return this.kontraktMediatorService.kontrakt.isVacsAvraknatOrAvslutat;
    }

    protected addSkogsvard() {
        this.kontraktMediatorService.openOneOfSkogsvardCreateDialog();
    }

    protected onToggle(expanded: boolean) {
        if (!expanded) {
            this.neverAutoExpandOnNewSkogsvardCreatedAgain = true;
        }
    }

    protected trackByFn(_index: number, item: EntityDTO) {
        return item.guid;
    }
}
